import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { NgFor, NgIf } from '@angular/common';
import { GridApi, GridReadyEvent, Module, ViewportChangedEvent } from '@ag-grid-community/core';
import { AgGridModule } from '@ag-grid-community/angular';
import moment from 'moment';
import { firstValueFrom, Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';

import {
	DmtFeasibilityRequest, DmtFeasibilityResponse, EfeasibilityInfo,
	FeasibilityErrorResponse,
	ProductCategoryDisplay, SubCategory
} from '../../../models/efeasibility';
import { LoadingSpinnerComponent } from '../../../../shared/components/utils/loading-spinner.component';
import { clientSideAgGridModules, CommonGridOptions } from '../../../../shared/datacenter/grid-options';
import { CommonColDef } from '../../../../shared/datacenter/column-defs';
import { ColumnType } from '../../../../shared/datacenter/column-type';
import { CheckboxHeaderComponent } from '../../../../shared/datacenter/header-components/checkbox-header.component';
import { TranslationService } from '../../../../shared/services/translation.service';
import { EfeasibilityService } from '../../../services/efeasibility.service';
import { WorkerService } from '../../../../shared/services/worker.service';
import { NumberPipe } from '../../../../shared/pipes/number.pipe';
import {
	DatacenterNumberFilterComponent
} from '../../../../shared/datacenter/filter-type/datacenter-number-filter.component';
import {
	DatacenterTextFilterComponent
} from '../../../../shared/datacenter/filter-type/datacenter-text-filter.component';
import { BocClient } from '../../../../shared/models/boc-client-transac';
import { ErrorResponse } from '../../../../fastlane/models/fastlane.model';
import { DialogErrorComponent } from '../../../../shared/components/error-dialog/dialog-error.component';

@Component({
	selector: 'copo-efeasibility-first-screen',
	templateUrl: './efeasibility-first-screen.component.html',
	styleUrl: './efeasibility-first-screen.component.css',
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		LoadingSpinnerComponent,
		TranslateModule,
		MatInputModule,
		MatFormFieldModule,
		FormsModule,
		ReactiveFormsModule,
		MatIconModule,
		NgFor,
		NgIf,
		MatButtonModule,
		MatSelectModule,
		AgGridModule,
		NumberPipe
	]
})
export class EfeasibilityFirstScreenComponent implements OnDestroy, OnChanges {
	@Input() efeasibilityItems: EfeasibilityInfo[] = [];
	@Input() showResults = false;
	@Input() productCategories?: ProductCategoryDisplay[];
	@Input() soldTos?: BocClient[];

	@Output() readonly soldToNameControlChanged = new EventEmitter<string>();
	@Output() readonly productCategoryChanged = new EventEmitter<string>();
	@Output() readonly productSubCategoryChanged = new EventEmitter<string>();
	@Output() readonly steelgradesChanged = new EventEmitter<{ steelgrades: Record<string, string[]>;
		standardSteelgrades: string[]; }>();

	formGroup: FormGroup;
	filteredSteelgrades: string[] = [];
	filteredStandardSteelgrades: string[] = [];
	isSubmitDisabled = true;
	exportInProgress = false;
	formatedDataToExport: (string | number | undefined)[][] = [];
	productSubCategories: SubCategory[] = [];
	steelgrades: Record<string, string[]> = {};
	gridOptions: CommonGridOptions<EfeasibilityInfo> | undefined;
	gridApi?: GridApi<EfeasibilityInfo>;
	clientSideModules: Module[] = clientSideAgGridModules;
	formSubscription: Subscription | undefined;
	filteredCount = 0;
	isRequesting = false;

	constructor(
		private readonly translationService: TranslationService,
		readonly workerService: WorkerService,
		private readonly efeasibilityService: EfeasibilityService,
		private readonly dialog: MatDialog
	) {
		this.formGroup = new FormGroup({
			soldToName: new FormControl('', Validators.required),
			productCategory: new FormControl({ value: '', disabled: true }, Validators.required),
			productSubCategory: new FormControl({ value: '', disabled: true }, Validators.required),
			standardSteelgrade: new FormControl({ value: '', disabled: true }, Validators.required),
			steelgrade: new FormControl({ value: '', disabled: true }, Validators.required),
			width: new FormControl({ value: '', disabled: true }, [
				Validators.required,
				Validators.pattern(/^\d+(\.\d+)?$/)
			]),
			thickness: new FormControl({ value: '', disabled: true }, [
				Validators.required,
				Validators.pattern(/^\d+(\.\d+)?$/)
			])
		});
	}

	ngOnChanges() {
		this.setRowData();
	}

	setRowData() {
		if (this.efeasibilityItems.length > 0) {
			this.filteredCount = this.efeasibilityItems.length;
			this.initAgGrid(this.efeasibilityItems);
			if (this.gridApi) {
				this.gridApi.setGridOption('rowData', this.efeasibilityItems);
			}
		}
	}

	refreshGrid() {
		if (this.gridApi) {
			this.gridApi.setFilterModel(null);
		}
	}

	onSoldToNameChange() {
		const soldToNameControl = this.formGroup.get('soldToName');
		const productCategoryControl = this.formGroup.get('productCategory');
		const subCategoryControl = this.formGroup.get('productSubCategory');
		const standardSteelgradeControl = this.formGroup.get('standardSteelgrade');
		const steelgradeControl = this.formGroup.get('steelgrade');
		const widthControl = this.formGroup.get('width');
		const thicknessControl = this.formGroup.get('thickness');

		if (this.soldTos && soldToNameControl?.value) {
			this.soldToNameControlChanged.emit(<string>soldToNameControl.value);
			productCategoryControl?.enable();
			productCategoryControl?.reset();
			subCategoryControl?.reset();
			standardSteelgradeControl?.reset();
			steelgradeControl?.reset();
			widthControl?.reset();
			thicknessControl?.reset();
			subCategoryControl?.disable();
			standardSteelgradeControl?.disable();
			steelgradeControl?.disable();
			this.productCategoryChanged.emit(undefined);
			this.productSubCategoryChanged.emit(undefined);
			this.updateFieldStates();
			this.isSubmitDisabled = true;
		} else {
			productCategoryControl?.disable();
			subCategoryControl?.disable();
			standardSteelgradeControl?.disable();
			steelgradeControl?.disable();
			this.productCategoryChanged.emit(undefined);
			this.productSubCategoryChanged.emit(undefined);
		}
	}

	onProductCategoryChange() {
		const soldToNameControl = this.formGroup.get('soldToName');
		const productCategoryControl = this.formGroup.get('productCategory');
		const subCategoryControl = this.formGroup.get('productSubCategory');
		const standardSteelgradeControl = this.formGroup.get('standardSteelgrade');
		const steelgradeControl = this.formGroup.get('steelgrade');
		const widthControl = this.formGroup.get('width');
		const thicknessControl = this.formGroup.get('thickness');

		if (soldToNameControl?.value && this.productCategories && productCategoryControl?.value) {
			for (const item of this.productCategories) {
				if (item.productCategoryCode === productCategoryControl.value) {
					this.productSubCategories = item.productSubCategories;
				}
			}
			this.productCategoryChanged.emit(<string>productCategoryControl.value);
			subCategoryControl?.enable();
			subCategoryControl?.reset();
			standardSteelgradeControl?.reset();
			steelgradeControl?.reset();
			widthControl?.reset();
			thicknessControl?.reset();
			standardSteelgradeControl?.disable();
			steelgradeControl?.disable();
			this.productSubCategoryChanged.emit(undefined);
			this.isSubmitDisabled = true;
			this.updateFieldStates();
		} else {
			subCategoryControl?.disable();
			standardSteelgradeControl?.disable();
			steelgradeControl?.disable();
			this.productCategoryChanged.emit(undefined);
		}
	}

	async onSubCategoryChange() {
		const productCategoryControl = this.formGroup.get('productCategory');
		const productSubCategoryControl = this.formGroup.get('productSubCategory');
		const standardSteelgradeControl = this.formGroup.get('standardSteelgrade');
		const steelgradeControl = this.formGroup.get('steelgrade');
		const widthControl = this.formGroup.get('width');
		const thicknessControl = this.formGroup.get('thickness');

		if (productCategoryControl?.value && productSubCategoryControl?.value) {
			standardSteelgradeControl?.enable();
			standardSteelgradeControl?.reset();
			steelgradeControl?.reset();
			widthControl?.reset();
			thicknessControl?.reset();
			steelgradeControl?.disable();
			this.isSubmitDisabled = true;
			await this.getSteelgrades();
			this.productSubCategoryChanged.emit(<string>productSubCategoryControl.value);
			this.updateFieldStates();
		} else {
			standardSteelgradeControl?.disable();
			steelgradeControl?.disable();
			this.productSubCategoryChanged.emit(undefined);
			this.steelgradesChanged.emit(undefined);
		}
	}

	async getSteelgrades() {
		const soldToId = <string> this.formGroup.get('soldToName')?.value;
		const paddedSoldTo = String(soldToId).padStart(10, '0');
		const productSubCategoryCode = <string> this.formGroup.get('productSubCategory')?.value;
		try {
			const { steelgrades: fetchedSteelgrades, standardSteelgrades: fetchedStandardSteelgrades } =
				await this.efeasibilityService.getSteelgrades(paddedSoldTo, productSubCategoryCode);
			this.filteredStandardSteelgrades = fetchedStandardSteelgrades;
			this.steelgrades = fetchedSteelgrades;

			this.steelgradesChanged.emit({
				steelgrades: this.steelgrades,
				standardSteelgrades: this.filteredStandardSteelgrades
			});
		} catch (err) {
			console.error(err);
			const errorResponse = <ErrorResponse> err;
			const { errorMessage } = <FeasibilityErrorResponse> <unknown>errorResponse.error;
			const sub = this.dialog.open(DialogErrorComponent);
			sub.componentInstance.dialogError = errorMessage;
			const standardSteelgradeControl = this.formGroup.get('standardSteelgrade');
			standardSteelgradeControl?.disable();
			this.filteredStandardSteelgrades = [];
			this.steelgrades = {};
			this.steelgradesChanged.emit({ steelgrades: {}, standardSteelgrades: [] });
		}
	}

	onStandardSteelgradeChange() {
		const standardSteelgradeControl = this.formGroup.get('standardSteelgrade');
		const steelgradeControl = this.formGroup.get('steelgrade');
		const widthControl = this.formGroup.get('width');
		const thicknessControl = this.formGroup.get('thickness');

		if (standardSteelgradeControl?.value) {
			steelgradeControl?.enable();
			steelgradeControl?.reset();
			widthControl?.reset();
			thicknessControl?.reset();
			this.filteredSteelgrades = this.steelgrades[<string>standardSteelgradeControl.value];
			this.isSubmitDisabled = true;
			this.updateFieldStates();
		} else {
			steelgradeControl?.disable();
		}
	}

	onSteelgradeChange() {
		const steelgradeControl = this.formGroup.get('steelgrade');
		const widthControl = this.formGroup.get('width');
		const thicknessControl = this.formGroup.get('thickness');
		if (steelgradeControl?.value) {
			widthControl?.reset();
			thicknessControl?.reset();
			this.isSubmitDisabled = true;
			this.updateFieldStates();
		} else {
			widthControl?.disable();
			thicknessControl?.disable();
		}
	}

	onWidthChange(event: Event) {
		const { value } = <HTMLInputElement> event.target;
		this.checkPattern(value);
	}

	checkPattern(value: string) {
		const pattern = /^\d+(\.\d+)?$/;
		this.isSubmitDisabled = pattern.test(value);
	}

	onThicknessChange(event: Event) {
		const { value } = <HTMLInputElement> event.target;
		this.checkPattern(value);
	}

	updateFieldStates() {
		const controls = [
			'soldToName',
			'productCategory',
			'productSubCategory',
			'standardSteelgrade',
			'steelgrade'
		];
		const shouldEnableFields = controls.every(control => Boolean(this.formGroup.get(control)?.value));

		if (shouldEnableFields) {
			this.formGroup.get('width')?.enable();
			this.formGroup.get('thickness')?.enable();
		} else {
			this.formGroup.get('width')?.disable();
			this.formGroup.get('thickness')?.disable();
		}
	}

	getButtonStatus(): boolean {
		const controls = [
			'soldToName',
			'productCategory',
			'productSubCategory',
			'standardSteelgrade',
			'steelgrade',
			'width',
			'thickness'
		];
		return controls.every(control => Boolean(this.formGroup.get(control)?.value));
	}

	async onRequestFeasibility() {
		if (this.isRequesting) {
			return;
		}
		this.isRequesting = true;
		this.refreshGrid();
		const soldToId = <string> this.formGroup.get('soldToName')?.value;
		const productCategoryCode = <string> this.formGroup.get('productCategory')?.value;
		const productSubCategoryCode = <string> this.formGroup.get('productSubCategory')?.value;
		const soldToName = this.soldTos
			? this.soldTos.find(
				soldToDetails => soldToDetails.businessId === this.formGroup.get('soldToName')?.value
			)?.name ?? '' : '';
		const productCategory = this.productCategories
			? this.productCategories.find(
				cat => cat.productCategoryCode === this.formGroup.get('productCategory')?.value
			)?.productCategory ?? '' : '';
		const productSubCategory = this.productSubCategories.find(
			cat => cat.productSubCategoryCode === <string> this.formGroup.get('productSubCategory')?.value
		)?.productSubCategory ?? '';
		const standardSteelGrade = <string> this.formGroup.get('standardSteelgrade')?.value;
		const steelGrade = <string> this.formGroup.get('steelgrade')?.value;
		const width = <number> this.formGroup.get('width')?.value;
		const thickness = <number> this.formGroup.get('thickness')?.value;
		const matchingSoldTo = this.soldTos?.find(
			soldToDetails => soldToDetails.businessId === soldToId
		);
		const matchingCategory = this.productCategories?.find(
			category => category.productCategory === productCategory
		);
		const matchingSubCategory = this.productSubCategories.find(
			category => category.productSubCategory === productSubCategory
		);
		const matchingStandardSteelgrades = this.filteredStandardSteelgrades;
		const matchingSteelgrades = this.steelgrades[standardSteelGrade];

		const isDataAvailable = matchingSoldTo && matchingCategory && matchingSubCategory
			&& matchingStandardSteelgrades.includes(standardSteelGrade) && matchingSteelgrades.includes(steelGrade);

		const efeasibilityRequest: DmtFeasibilityRequest = {
			productCategoryCode,
			productSubCategoryCode,
			widthValue: <string> <unknown>width,
			thicknessValue: <string> <unknown>thickness,
			steelGradeStandardName: standardSteelGrade,
			steelGradeName: steelGrade
		};

		let response: DmtFeasibilityResponse | undefined;
		try {
			response = await firstValueFrom(this.efeasibilityService.getDmtFeasibility(efeasibilityRequest));
		} catch (error) {
			console.error(error);
			response = undefined;
		}

		const ANSWER_TECHNICAL = this.translationService.getTR('product.feasibility.technical-error');
		const ANSWER_NOT_FEASIBLE = this.translationService.getTR('product.feasibility.not-feasible');
		let answer: string;

		if (!response && isDataAvailable) {
			answer = ANSWER_TECHNICAL;
		} else if (!isDataAvailable) {
			answer = ANSWER_NOT_FEASIBLE;
		} else if (response?.answer) {
			answer = response.answer;
		} else {
			answer = ANSWER_NOT_FEASIBLE;
		}

		const efeasibility: EfeasibilityInfo = {
			soldToId,
			soldToName,
			productCategory,
			productSubCategory,
			standardSteelGrade,
			steelGrade,
			thickness,
			width,
			answer
		};
		this.efeasibilityItems.push(efeasibility);
		try {
			await this.efeasibilityService.addEfeasibility([efeasibility]).then(() => {
				this.initAgGrid(this.efeasibilityItems);
			});
		} catch (error) {
			console.error('Error from http put current user detail', error);
		}
		if (this.gridApi) {
			this.gridApi.setGridOption('rowData', this.efeasibilityItems);
		}
		this.showResults = true;
		this.filteredCount = this.efeasibilityItems.length;
		this.isSubmitDisabled = false;
		this.isRequesting = false;
	}

	onGridReady(params: GridReadyEvent) {
		this.gridApi = params.api;
		this.gridApi.sizeColumnsToFit();
	}

	onViewportChanged(params: ViewportChangedEvent) {
		params.api.sizeColumnsToFit();
	}

	canDownload() {
		return !this.exportInProgress && this.efeasibilityItems.length > 0;
	}

	onBtExport() {
		if (!this.gridApi) {
			return;
		}
		const filteredRows: EfeasibilityInfo[] = [];

		this.gridApi.forEachNodeAfterFilterAndSort(node => {
			if (node.data && this.efeasibilityItems.includes(node.data)) {
				filteredRows.push(node.data);
			}
		});
		if (filteredRows.length === 0) {
			return;
		}
		this.exportInProgress = true;
		const columnTypes = this.getColumnTypes();
		const disclaimerText = [
			this.translationService.getTR('efeasibility-disclaimer'),
			this.translationService.getTR('efeasibility-disclaimer.end')
		].join('\n\n');

		const formattedData = [
			[disclaimerText],
			...Array.from({ length: 8 }).fill([]),
			columnTypes,
			...filteredRows.map(feasibility =>
				columnTypes.map(columnDef =>
					this.getColumnValue(columnDef, feasibility)))
		];

		this.formatedDataToExport = this.formatExportedData(columnTypes, filteredRows);
		const fileName = `Feasibility-${ moment().format('YYYY MM DD') }.xlsx`;
		const worker = this.workerService.createWorkerExcel();
		worker.addEventListener('message', ({ data }: { data: string }) => {
			this.handleWorkerMessage(data, worker, fileName);
		});
		const sheets = [
			{
				fileName,
				rows: formattedData,
				workSheetName: 'Feasibility',
				columnTypes,
				disclaimerConfig: {
					range: 'A1:H8',
					startRow: 9
				}
			}
		];
		worker.postMessage({
			sheets
		});
	}

	ngOnDestroy(): void {
		if (this.formSubscription) {
			this.formSubscription.unsubscribe();
		}
	}

	onFilterChanged() {
		if (this.gridApi) {
			this.filteredCount = this.gridApi.getDisplayedRowCount();
		}
	}

	private initAgGrid(efeasibilities: EfeasibilityInfo[]) {
		this.gridOptions = new CommonGridOptions<EfeasibilityInfo>({
			translationService: this.translationService,
			domLayout: 'autoHeight',
			rowData: efeasibilities,
			headerHeight: 70,
			onFilterChanged: this.onFilterChanged.bind(this),
			columnDefs: [
				new CommonColDef<EfeasibilityInfo>({
					headerName: this.translationService.getTR('feasibility-status'),
					headerComponent: CheckboxHeaderComponent,
					field: 'answer',
					filter: false,
					suppressHeaderMenuButton: true,
					columnType: ColumnType.TEXT_COLUMN,
					cellRenderer: (params: { value: string }) => this.statusCellRenderer(params),
					width: 70
				}),
				new CommonColDef<EfeasibilityInfo>({
					headerName: this.translationService.getTR('guarantee.customer.name'),
					headerComponent: CheckboxHeaderComponent,
					field: 'soldToName',
					filter: DatacenterTextFilterComponent,
					filterParams: { filterOptions: ['contains'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN,
					sortable: true,
				}),
				new CommonColDef<EfeasibilityInfo>({
					headerName: this.translationService.getTR('datacenter-filter.product-category'),
					headerComponent: CheckboxHeaderComponent,
					field: 'productCategory',
					filter: DatacenterTextFilterComponent,
					filterParams: { filterOptions: ['contains'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN,
					sortable: true,
				}),
				new CommonColDef<EfeasibilityInfo>({
					headerName: this.translationService.getTR('datacenter.offer.subcategory'),
					headerComponent: CheckboxHeaderComponent,
					field: 'productSubCategory',
					filter: DatacenterTextFilterComponent,
					filterParams: { filterOptions: ['contains'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN,
					sortable: true,
				}),
				new CommonColDef<EfeasibilityInfo>({
					headerName: this.translationService.getTR('standard-steelgrade'),
					headerComponent: CheckboxHeaderComponent,
					field: 'standardSteelGrade',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN,
					sortable: true,
				}),
				new CommonColDef<EfeasibilityInfo>({
					headerName: this.translationService.getTR('dashboard.steelgrade'),
					headerComponent: CheckboxHeaderComponent,
					field: 'steelGrade',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN,
					sortable: true,
				}),
				new CommonColDef<EfeasibilityInfo>({
					headerName: this.translationService.getTR('shipment.filter.width'),
					headerComponent: CheckboxHeaderComponent,
					isMandatoryOnLeft: false,
					field: 'width',
					filter: DatacenterNumberFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.NUMBER_COLUMN,
					sortable: true,
					cellStyle: { textAlign: 'left' }
				}),
				new CommonColDef<EfeasibilityInfo>({
					headerName: this.translationService.getTR('shipment.filter.thickness'),
					headerComponent: CheckboxHeaderComponent,
					isMandatoryOnLeft: false,
					field: 'thickness',
					filter: DatacenterNumberFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.NUMBER_COLUMN,
					sortable: true,
					cellStyle: { textAlign: 'left' }
				}),
			]
		});
	}

	private statusCellRenderer(params: { value: string }): string {
		if (!params.value) {
			return '';
		}
		const status = params.value.trim().toLowerCase();
		if (status === 'technical error') {
			return `<div style="display: flex; justify-content: space-between;
			height: 100%; padding-left: 25px; margin-top: 5px;">
			<svg class="error-icon" width="20" height="20" viewBox="0 0 24 24">
			<path fill="orange" d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
			</svg>
			</div>`;
		}
		const color = this.getStatusColor(status);
		return `<div style="display: flex; justify-content: space-between;
			height: 100%; padding-left: 25px; margin-top: 5px;">
			<div style="width: 20px; height: 20px; border-radius: 50%; background-color: ${ color };"></div>
			</div>`;
	}

	private getStatusColor(status: string): string {
		switch (status) {
		case 'feasible':
			return 'green';
		case 'to consult':
			return 'yellow';
		case 'not feasible':
			return 'red';
		default:
			return 'grey';
		}
	}

	private getColumnTypes(): string[] {
		return [
			this.translationService.getTR('feasibility-status'),
			this.translationService.getTR('guarantee.customer.name'),
			this.translationService.getTR('datacenter-filter.product-category'),
			this.translationService.getTR('datacenter.offer.subcategory'),
			this.translationService.getTR('standard-steelgrade'),
			this.translationService.getTR('dashboard.steelgrade'),
			this.translationService.getTR('shipment.filter.width'),
			this.translationService.getTR('shipment.filter.thickness')
		];
	}

	private handleWorkerMessage(data: string, worker: Worker, fileName: string) {
		this.exportInProgress = false;
		const link = document.createElement('a');
		link.download = fileName;
		link.href = data;
		link.append(document.createTextNode(fileName));
		document.body.append(link);
		link.click();
		link.remove();
		URL.revokeObjectURL(data);
		worker.terminate();
	}

	private formatExportedData(
		columnTypes: string[],
		efeasibilities: EfeasibilityInfo[]
	) {
		return [
			columnTypes,
			...efeasibilities.map(feasibility => columnTypes.map(columnDef =>
				this.getColumnValue(columnDef, feasibility)))
		];
	}

	private getColumnValue(columnDef: string, feasibility: EfeasibilityInfo): string | number | undefined {
		switch (columnDef) {
		case this.translationService.getTR('feasibility-status'):
			return feasibility.answer;
		case this.translationService.getTR('guarantee.customer.name'):
			return feasibility.soldToName;
		case this.translationService.getTR('datacenter-filter.product-category'):
			return feasibility.productCategory;
		case this.translationService.getTR('datacenter.offer.subcategory'):
			return feasibility.productSubCategory;
		case this.translationService.getTR('standard-steelgrade'):
			return feasibility.standardSteelGrade;
		case this.translationService.getTR('dashboard.steelgrade'):
			return feasibility.steelGrade;
		case this.translationService.getTR('shipment.filter.width'):
			return feasibility.width;
		case this.translationService.getTR('shipment.filter.thickness'):
			return feasibility.thickness;
		default:
			return '';
		}
	}
}
